import React from 'react';
import { Box, Container, Typography, Divider, Button, Stack, Menu, MenuItem } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  ShoppingCart as ShoppingCartIcon,
  ReceiptLong as ReceiptLongIcon,
  Logout as LogoutIcon,
  Home as HomeIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  VideoCall as VideoCallIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';

// Default header image from public assets
const defaultHeaderImage = '/assets/images/header_banner3.png';

/**
 * PageLayout
 * - Provides the app-wide gradient background and a centered white card container
 * - Shows a full-width header image (within the card) with rounded corners
 * - Wraps page content inside a max-width Container for readability
 *
 * Props:
 * - children: React nodes to render inside the layout
 * - headerImage: optional custom image path; defaults to kiosk banner
 * - contentMaxWidth: MUI Container maxWidth for inner content (e.g., 'md' | 'lg' | 'xl')
 * - showHeaderImage: whether to display the header image (default: true)
 * - showOrgHeader: whether to show the org/portal title block (default: true)
 * - orgTitle: main title line for the organization
 * - orgAddress: secondary line for address
 * - portalTitle: large section title below org lines
 * - contentPadding: sx value for inner padding (default uses sensible spacing)
 */
const PageLayout = ({
  children,
  headerImage = defaultHeaderImage,
  contentMaxWidth = 'md',
  showHeaderImage = true,
  showOrgHeader = true,
  orgTitle = 'India Cultural Center and Temple',
  orgAddress = '12005 Hwy 64, Eads, TN 38028',
  portalTitle = 'ICCT Donations Portal',
  contentPadding = { xs: 1, sm: 2 },
  showAdminActions = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { staff, logout } = useAuth();
  const perms = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const isAdminPermission = perms.includes('admin:full') || String(staff?.role || '').toLowerCase() === 'admin';
  const hasAny = (keys) => keys.some(k => perms.includes(k));
  const hasOrders = isAdminPermission || hasAny(['orders:view', 'orders:edit', 'orders:create', 'orders:delete']);
  const hasPayments = isAdminPermission || hasAny(['orders:view', 'orders:edit', 'orders:create']);
  const hasDevotees = isAdminPermission || hasAny(['devotees:view', 'devotees:edit', 'devotees:create', 'devotees:delete']);
  const hasSponsorsReports = isAdminPermission || hasAny(['sponsors_reports:access', 'reports:view']);
  const hasSettings = isAdminPermission || hasAny(['settings:access']);
  const hasUsersManage = isAdminPermission || hasAny(['users:manage']);
  const hasMeetingsAccess = isAdminPermission || hasAny(['meetings:access']);
  const hasEventReports = isAdminPermission || hasAny(['event_reports:access', 'reports:view']);
  const hasReportsMenu = hasEventReports || hasSponsorsReports;
  // Show the Manage entry to anyone who can reach at least one Manage card — including calendar:manage
  // (calendar events) and the roster-viewing perms (rosters). Individual cards still gate themselves.
  const hasManage = isAdminPermission
    || hasAny(['reports:view', 'calendar:manage', 'communications:access', 'event_reports:access', 'sponsors_reports:access'])
    || hasUsersManage;
  const hasTempleAccess = isAdminPermission || hasAny(['temple:access']);
  const hasKotilingaAccess = isAdminPermission || hasAny(['kotilinga:access']);
  // Temple & Kotilinga tabs gate purely on their own access (which finance:access grants). Communications
  // no longer opens Temple, and Kotilinga no longer piggybacks on pledges/analytics/manage.
  const hasTempleStack = hasTempleAccess;
  const hasKotilingaStack = hasKotilingaAccess;

  const [kotilingaAnchorEl, setKotilingaAnchorEl] = React.useState(null);
  const kotilingaMenuOpen = Boolean(kotilingaAnchorEl);
  const [templeAnchorEl, setTempleAnchorEl] = React.useState(null);
  const templeMenuOpen = Boolean(templeAnchorEl);
  const [paymentsAnchorEl, setPaymentsAnchorEl] = React.useState(null);
  const paymentsMenuOpen = Boolean(paymentsAnchorEl);
  const handlePaymentsOpen = (event) => { setPaymentsAnchorEl(event.currentTarget); };
  const handlePaymentsClose = () => { setPaymentsAnchorEl(null); };

  const handleKotilingaOpen = (event) => {
    setKotilingaAnchorEl(event.currentTarget);
  };

  const handleKotilingaClose = () => {
    setKotilingaAnchorEl(null);
  };

  const handleTempleOpen = (event) => {
    setTempleAnchorEl(event.currentTarget);
  };

  const handleTempleClose = () => {
    setTempleAnchorEl(null);
  };

  const path = location?.pathname || '';
  const search = location?.search || '';
  const isSettingsActive = path.startsWith('/settings');

  // When on a scoped page (pledges, analytics, reports), highlight Temple or Kotilinga nav based on query
  const searchParams = new URLSearchParams(search);
  const scopeParam = (searchParams.get('scope') || '').toLowerCase();
  const viewParam = (searchParams.get('view') || '').toLowerCase();
  let stackKey = null;
  if (path.startsWith('/payments')) stackKey = 'payments';
  else if (path.startsWith('/temple')) stackKey = 'temple';
  else if (path.startsWith('/kotilinga')) stackKey = 'kotilinga';
  else if (path.startsWith('/pledges') && (scopeParam === 'temple' || scopeParam === 'kotilinga')) stackKey = scopeParam;
  else if (path.startsWith('/analytics') && (viewParam === 'temple' || viewParam === 'kotilinga')) stackKey = viewParam;
  else if (path.startsWith('/reports') && (scopeParam === 'temple' || scopeParam === 'kotilinga')) stackKey = scopeParam;

  const activeKey = stackKey !== null
    ? stackKey
    : path.startsWith('/dashboard')
      ? null
      : path.startsWith('/post-payment')
        ? 'orders'
        : path.startsWith('/devotees')
          ? 'devotees'
          : path.startsWith('/orders')
            ? 'orders'
            : path.startsWith('/pledges')
              ? 'pledges'
              : path.startsWith('/kotilinga')
                ? 'kotilinga'
                : path.startsWith('/temple')
                  ? 'temple'
                  : path.startsWith('/reports')
                    ? 'reports'
                    : path.startsWith('/manage')
                      ? 'manage'
                      : path.startsWith('/analytics')
                        ? 'analytics'
                        : path.startsWith('/meetings')
                          ? 'meetings'
                          : null;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', pt: '20px', pb: { xs: 2, md: 4 }, background: 'linear-gradient(135deg, #E3F2FD 0%, #F3E5F5 50%, #FFF8E1 100%)' }}>
      {showOrgHeader && (
        <Box sx={{ textAlign: 'center', mb: 2, width: '95%', maxWidth: '95%', mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src="/assets/images/ICCT-Logo-trans.webp"
              alt="ICCT Logo"
              sx={{ height: 40, width: 'auto' }}
            />
            <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0.5 }}>{portalTitle}</Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ width: '95%', maxWidth: '95%', mx: 'auto', px: { xs: 2, sm: 4 }, pt: 0, pb: { xs: 1, sm: 2 }, background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(246,249,255,0.94) 100%)', border: '1px solid', borderColor: 'divider', borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
        {showHeaderImage && (
          <Box
            component="img"
            src={headerImage}
            alt="Page Header"
            sx={{
              width: '100%',
              maxHeight: '300px',
              objectFit: 'cover',
              display: 'block',
              marginBottom: 2,
              mx: 'auto',
              borderRadius: 3,
            }}
          />
        )}

        {showAdminActions && (
          <Box sx={{ pt: '5px', mb: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1} sx={{ flex: 1, flexWrap: 'nowrap', alignItems: 'center' }}>
      <Button
                variant={'outlined'}
                color={'inherit'}
                size="small"
                startIcon={<HomeIcon />}
                onClick={() => navigate(isAdminPermission ? '/dashboard' : '/welcome')}
                sx={{ px: 1.5, py: 0.75, whiteSpace: 'nowrap' }}
              >
                Home
              </Button>
              {hasDevotees && (
                <Button
                  variant={activeKey === 'devotees' ? 'contained' : 'outlined'}
                  color={activeKey === 'devotees' ? 'primary' : 'inherit'}
                  size="small"
                  startIcon={<PeopleIcon />}
                  onClick={() => navigate('/devotees')}
                  sx={{ px: 1.5, py: 0.75, whiteSpace: 'nowrap' }}
                >
                  Devotees
                </Button>
              )}
              {hasOrders && (
                <Button
                  variant={activeKey === 'orders' ? 'contained' : 'outlined'}
                  color={activeKey === 'orders' ? 'primary' : 'inherit'}
                  size="small"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => navigate('/orders')}
                  sx={{ px: 1.5, py: 0.75, whiteSpace: 'nowrap' }}
                >
                  Orders
                </Button>
              )}
              {hasPayments && (
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant={activeKey === 'payments' ? 'contained' : 'outlined'}
                    color={activeKey === 'payments' ? 'primary' : 'inherit'}
                    size="small"
                    startIcon={<PaymentsIcon />}
                    onClick={handlePaymentsOpen}
                    onMouseEnter={handlePaymentsOpen}
                    sx={{ px: 1.5, py: 0.75, whiteSpace: 'nowrap' }}
                  >
                    Payments
                  </Button>
                  <Menu
                    anchorEl={paymentsAnchorEl}
                    open={paymentsMenuOpen}
                    onClose={handlePaymentsClose}
                    MenuListProps={{ onMouseLeave: handlePaymentsClose }}
                  >
                    <MenuItem
                      onClick={() => {
                        handlePaymentsClose();
                        navigate('/payments/recurring');
                      }}
                    >
                      Recurring Subscriptions
                    </MenuItem>
                    {/* Hidden from the menu (soft launch) — reachable by direct URL:
                        Payment Alerts:          /payments/alerts
                        Re-add these MenuItems when ready to surface them. */}
                    <MenuItem
                      onClick={() => {
                        handlePaymentsClose();
                        navigate('/payments/authnet');
                      }}
                    >
                      Authorize.Net Reconciliation
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handlePaymentsClose();
                        navigate('/payments/stripe/reconciliation');
                      }}
                    >
                      Stripe Reconciliation
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handlePaymentsClose();
                        navigate('/payments/stripe/payouts');
                      }}
                    >
                      Stripe Payouts
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handlePaymentsClose();
                        navigate('/payments/paysimple');
                      }}
                    >
                      PaySimple Reconciliation
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handlePaymentsClose();
                        navigate('/payments/zelle-reconciliation');
                      }}
                    >
                      Zelle Reconciliation
                    </MenuItem>
                  </Menu>
                </Box>
              )}
              {hasTempleStack && (
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant={activeKey === 'temple' ? 'contained' : 'outlined'}
                    color={activeKey === 'temple' ? 'primary' : 'inherit'}
                    size="small"
                    startIcon={<AssessmentIcon />}
                    onClick={handleTempleOpen}
                    onMouseEnter={handleTempleOpen}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      whiteSpace: 'nowrap',
                      ...(activeKey !== 'temple' && { bgcolor: 'rgba(255, 152, 0, 0.08)', borderColor: 'rgba(255, 152, 0, 0.4)', '&:hover': { bgcolor: 'rgba(255, 152, 0, 0.14)' } }),
                    }}
                  >
                    Temple
                  </Button>
                  <Menu
                    anchorEl={templeAnchorEl}
                    open={templeMenuOpen}
                    onClose={handleTempleClose}
                    MenuListProps={{
                      onMouseLeave: handleTempleClose,
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleTempleClose();
                        navigate('/temple/sponsorships');
                      }}
                    >
                      Dashboard
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleTempleClose();
                        navigate('/temple/tree-revenue-report');
                      }}
                    >
                      Revenue Report
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleTempleClose();
                        navigate('/temple/pledges');
                      }}
                    >
                      Pledges
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleTempleClose();
                        navigate('/temple/analytics');
                      }}
                    >
                      Analytics
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleTempleClose();
                        navigate('/temple/campaigns');
                      }}
                    >
                      Campaigns
                    </MenuItem>
                    {hasSponsorsReports && (
                      <MenuItem
                        onClick={() => {
                          handleTempleClose();
                          navigate('/reports/sponsors?scope=temple');
                        }}
                      >
                        Sponsors Report
                      </MenuItem>
                    )}
                    {hasEventReports && (
                      <MenuItem
                        onClick={() => {
                          handleTempleClose();
                          navigate('/reports/event-reports?scope=temple');
                        }}
                      >
                        Events Report
                      </MenuItem>
                    )}
                  </Menu>
                </Box>
              )}
              {hasKotilingaStack && (
                <Box sx={{ position: 'relative' }}>
                  <Button
                    variant={activeKey === 'kotilinga' ? 'contained' : 'outlined'}
                    color={activeKey === 'kotilinga' ? 'primary' : 'inherit'}
                    size="small"
                    startIcon={<AssessmentIcon />}
                    onClick={handleKotilingaOpen}
                    onMouseEnter={handleKotilingaOpen}
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      whiteSpace: 'nowrap',
                      ...(activeKey !== 'kotilinga' && { bgcolor: 'rgba(0, 150, 136, 0.08)', borderColor: 'rgba(0, 150, 136, 0.4)', '&:hover': { bgcolor: 'rgba(0, 150, 136, 0.14)' } }),
                    }}
                  >
                    Kotilinga
                  </Button>
                  <Menu
                    anchorEl={kotilingaAnchorEl}
                    open={kotilingaMenuOpen}
                    onClose={handleKotilingaClose}
                    MenuListProps={{
                      onMouseLeave: handleKotilingaClose,
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleKotilingaClose();
                        navigate('/kotilinga/sponsorships');
                      }}
                    >
                      Dashboard
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleKotilingaClose();
                        navigate('/kotilinga/tree-revenue-report');
                      }}
                    >
                      Revenue Report
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleKotilingaClose();
                        navigate('/kotilinga/sri-chakram-locator');
                      }}
                    >
                      Linga Locator
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleKotilingaClose();
                        navigate('/kotilinga/pledges');
                      }}
                    >
                      Pledges
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleKotilingaClose();
                        navigate('/kotilinga/analytics');
                      }}
                    >
                      Analytics
                    </MenuItem>
                    {hasSponsorsReports && (
                      <MenuItem
                        onClick={() => {
                          handleKotilingaClose();
                          navigate('/reports/sponsors?scope=kotilinga');
                        }}
                      >
                        Sponsors Report
                      </MenuItem>
                    )}
                  </Menu>
                </Box>
              )}
              {hasManage && (
                <Button
                  variant={activeKey === 'manage' ? 'contained' : 'outlined'}
                  color={activeKey === 'manage' ? 'primary' : 'inherit'}
                  size="small"
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate('/manage')}
                  sx={{ px: 1.5, py: 0.75, whiteSpace: 'nowrap' }}
                >
                  Manage
                </Button>
              )}
              {hasMeetingsAccess && (
                <Button
                  variant={activeKey === 'meetings' ? 'contained' : 'outlined'}
                  color={activeKey === 'meetings' ? 'primary' : 'inherit'}
                  size="small"
                  startIcon={<VideoCallIcon />}
                  onClick={() => navigate('/meetings')}
                  sx={{ px: 1.5, py: 0.75, whiteSpace: 'nowrap' }}
                >
                  Meetings
                </Button>
              )}
              {/* Settings button dropped — its two items (Data & sync, System Log) now live under Manage. */}
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end" sx={{ flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Logged in as <strong>{staff?.name || staff?.email || 'User'}</strong>
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<LogoutIcon />}
                onClick={async () => {
                  await logout();
                  navigate('/login');
                }}
              >
                Logout
              </Button>
            </Stack>
          </Box>
        )}

        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 4 }, py: contentPadding, width: '100%' }}>
          {children}
        </Container>

        {/* Footer with organization info and confidentiality */}
        <Divider sx={{ mt: 3 }} />
        <Box sx={{ textAlign: 'center', mt: 2, pb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{orgTitle}</Typography>
          <Typography variant="body2" color="text.secondary">{orgAddress}</Typography>
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
            Confidential. For authorized ICCT use only. Do not share or distribute the portal content outside the organization.
          </Typography>
        </Box>

      </Box>
    </Box>
  );
};

export default PageLayout;
